import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/**
 * Bottom tab bar — the native app's primary navigation (replaces SiteHeader
 * inside the Capacitor shell). Four tabs, standard iOS/Android pattern:
 *
 *   Home        — hub: connections, leagues + the sidebar menu
 *   Drafts      — your real league draft rooms (board / AI coach)
 *   Mock Drafts — practice runs; the CPU drafts every team but yours
 *   Cheat Sheet — your own ranked board (multiple named sheets)
 *
 * History, so the same ground isn't re-litigated: News was removed
 * entirely; More collapsed into the Home sidebar; Profile split into Home
 * and Settings; Settings then left the bar for the top menu (one screen of
 * colour swatches did not deserve a permanent slot); and Rankings gave up
 * its slot to Cheat Sheet — you BUILD a cheat sheet all preseason, while
 * the expert board is reference. Sidebar tool pages light the Home tab.
 *
 * Safe-area aware: pads for the iPhone home indicator via
 * env(safe-area-inset-bottom). Hidden on the focused draft surfaces
 * (/drafts/:id) which runs its own full-screen chrome —
 * same rule SiteHeader uses on web.
 */

type Tab = {
  to: string;
  label: string;
  /** Route prefixes that light this tab up beyond its own path. */
  match: string[];
  icon: (active: boolean) => JSX.Element;
};

const stroke = (active: boolean) => ({
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: active ? 2.2 : 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

const TABS: Tab[] = [
  {
    to: '/',
    label: 'Home',
    // Everything reachable from the top MENU lights Home, since the menu is
    // anchored there. /cheatsheet moved OUT (it owns a tab now) and
    // /rankings moved IN (it became a menu destination).
    match: [
      '/',
      '/league',
      '/more',
      '/rankings',
      '/compare',
      '/rookies',
      '/players',
      '/trending',
      '/exposure',
      '/depth-charts',
      '/injuries',
      '/help',
      '/news',
    ],
    icon: (a) => (
      <svg viewBox="0 0 24 24" className="h-6 w-6" {...stroke(a)}>
        <path d="M3.5 10.5L12 3.8l8.5 6.7" />
        <path d="M5.5 9.2V20h13V9.2" />
        <path d="M9.8 20v-5.6h4.4V20" />
      </svg>
    ),
  },
  {
    to: '/drafts',
    label: 'Drafts',
    match: ['/drafts', '/dashboard'],
    icon: (a) => (
      <svg viewBox="0 0 24 24" className="h-6 w-6" {...stroke(a)}>
        <ellipse cx="12" cy="12" rx="9" ry="6.2" transform="rotate(-25 12 12)" />
        <path d="M8.8 13.8l6.4-3.6M10.5 10.5l3 4.9" />
      </svg>
    ),
  },
  {
    // Mock drafts are a first-class destination (owner brief), and Settings
    // moved OUT of the bar into the top menu — it was one screen of colour
    // swatches competing for a permanent slot with the core workflow.
    to: '/mocks',
    label: 'Mock Drafts',
    match: ['/mocks'],
    icon: (a) => (
      <svg viewBox="0 0 24 24" className="h-6 w-6" {...stroke(a)}>
        {/* Robot head — the CPU drafts every team but yours. */}
        <rect x="4.2" y="8" width="15.6" height="11" rx="3" />
        <path d="M12 8V4.9" />
        <circle cx="12" cy="3.9" r="1.2" />
        <path d="M9.2 12.4v1.6M14.8 12.4v1.6" />
      </svg>
    ),
  },
  // Cheat sheets sit LAST and REPLACED Rankings here (owner brief: "very
  // important"). Your own board is the thing you build all preseason and
  // reach for on draft night; the read-only expert list is reference, and
  // moved to the menu.
  {
    to: '/cheatsheet',
    label: 'Cheat Sheet',
    match: ['/cheatsheet'],
    icon: (a) => (
      <svg viewBox="0 0 24 24" className="h-6 w-6" {...stroke(a)}>
        {/* Clipboard with rank lines. */}
        <rect x="5" y="4.5" width="14" height="16" rx="2.5" />
        <path d="M9.2 4.5V3.4h5.6v1.1" />
        <path d="M8.6 10h6.8M8.6 13.4h6.8M8.6 16.8h4" />
      </svg>
    ),
  },
  // Compare Players (owner 2026-08-06). It was on the web nav and reachable
  // in the app, but had no tab, so the two surfaces disagreed on what the
  // product's primary tools are. Sits last: it is a tool you reach WITH two
  // players in mind, not a place you browse to, so it earns a tab without
  // outranking the board you build or the room you draft in.
  {
    to: '/compare',
    label: 'Compare',
    match: ['/compare'],
    icon: (a) => (
      <svg viewBox="0 0 24 24" className="h-6 w-6" {...stroke(a)}>
        {/* Two columns side by side, the shape of a head-to-head. */}
        <rect x="3.5" y="5" width="7" height="14" rx="1.8" />
        <rect x="13.5" y="5" width="7" height="14" rx="1.8" />
        <path d="M6 9.5h2M6 12.5h2M16 9.5h2M16 12.5h2" />
      </svg>
    ),
  },
];

/**
 * Should the bar be hidden after a scroll event? Pure so it can be
 * tested without a scrollable viewport (the dev browser pane pins
 * window.scrollY at 0 and cannot exercise this).
 *
 * Returns the next hidden state, or null to mean "no change, and do NOT
 * move the anchor" - that's what makes the deadzone accumulate instead
 * of swallowing slow scrolls.
 */
export function nextBarHidden(p: {
  y: number;
  lastY: number;
  viewportH: number;
  docH: number;
}): boolean | null {
  if (p.y < 72) return false; // near the top the bar is always available
  const dy = p.y - p.lastY;
  if (Math.abs(dy) < 8) return null; // jitter
  const atBottom = p.y + p.viewportH >= p.docH - 4;
  return dy > 0 && !atBottom;
}

export function MobileTabBar() {
  const location = useLocation();

  // Auto-hide on scroll DOWN, restore on scroll UP (owner brief) - the
  // bar costs ~90pt of a phone screen while reading a long list.
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        ticking.current = false;
        const y = window.scrollY;
        const next = nextBarHidden({
          y,
          lastY: lastY.current,
          viewportH: window.innerHeight,
          docH: document.documentElement.scrollHeight,
        });
        if (next === null) return; // deadzone: keep the anchor
        setHidden(next);
        lastY.current = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A new screen always starts with the bar visible.
  useEffect(() => {
    setHidden(false);
    lastY.current = window.scrollY;
  }, [location.pathname]);

  // Keep the draft ROOM distraction-free — it runs its own full-screen
  // chrome and a tab bar competes with the board.
  //
  // The new-draft wizard is NOT that. It is an ordinary form, and hiding
  // the bar there was a trap: the wizard's own "Cancel" is gated off on
  // native (the back-chrome removal), so /drafts/new had no tab bar AND no
  // cancel. Anyone opening it from the Home tile was stranded with only the
  // edge-swipe gesture, which is undiscoverable. Owner-reported.
  if (
    location.pathname !== '/drafts/new' &&
    /^\/drafts\/.+/.test(location.pathname)
  ) {
    return null;
  }

  return (
    <nav
      aria-label="App navigation"
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-chrome-ink/15 backdrop-blur transition-transform duration-200 ease-out ${
        hidden ? 'translate-y-full' : 'translate-y-0'
      }`}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        background: 'rgb(var(--de-chrome) / var(--de-chrome-alpha, 0.92))',
      }}
    >
      <div className="mx-auto flex max-w-lg items-stretch justify-around">
        {TABS.map((tab) => {
          const active = tab.match.some(
            (m) => location.pathname === m || location.pathname.startsWith(`${m}/`),
          );
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              aria-label={tab.label}
              aria-current={active ? 'page' : undefined}
              className={`relative flex flex-1 flex-col items-center gap-0.5 pb-1 pt-2 text-[11px] transition-colors ${
                active ? 'text-chrome-accent' : 'text-chrome-muted'
              }`}
            >
              {active ? (
                <span
                  aria-hidden
                  className="absolute inset-x-6 top-0 h-0.5 rounded-full bg-chrome-accent shadow-[0_0_8px_var(--de-accent-glow)]"
                />
              ) : null}
              {tab.icon(active)}
              {/* whitespace-nowrap: "Mock Drafts" is two words and would
                  otherwise wrap to a second line on a narrow phone, making
                  that one tab taller than its neighbours. */}
              <span
                className={`whitespace-nowrap ${active ? 'font-semibold' : ''}`}
              >
                {tab.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
